import styles from "./SignUp.module.css";
import { path, validateDomain, validateEmailId, validateName, validatePassword } from "../common/Common";
import { ChangeEvent, useEffect, useRef, useState, BaseSyntheticEvent } from "react";
import { AccountManager } from "../../workers/AccountManager";
import Button from "../common/Button";
import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import PolicyModal from "./PolicyModal";
import PrivacyModal from "./PrivacyModal";
import TextInput from "../common/TextInput";

/**
 * @author kmy
 * @date 2025-03-17
 * @remark 회원가입 페이지
 */

function SignUp() {
    const { ModalComponent, openModal, closeModal } = useModal();

    const [registerInfo, setRegisterInfo] = useState({
        id: "",
        host: "",
        platform: "1",
        password: "",
        password_check: "",
        name: "",
        year: "",
        month: "",
        day: "",
        sex: "1",
        policyAccepted: false, // 약관 동의
        privacyAccepted: false, // 개인정보 수집 동의
    });

    const selectEmailOptions = [
        { value: "", label: "직접 입력" },
        { value: "naver.com", label: "naver.com" },
        { value: "gmail.com", label: "gmail.com" },
        { value: "daum.net", label: "daum.net" },
    ];

    const [emailDisabled, setEmailDisabled] = useState(false);
    const [isValidHost, setIsValidHost] = useState(true);
    const [isValidDomain, setIsValidDomain] = useState(true);
    const [isValidPassword, setIsValidPassword] = useState(true);
    const [isPasswordMatch, setIsPasswordMatch] = useState(true);
    const [isValidName, setIsValidName] = useState(true);
    const handleFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        var key = e.target.name;
        var value = e.target.value;

        // 이메일 유효성 검사에 따라 중복확인 버튼 활성화 변경되도록 수정 | smyun | 250610
        // 유효성 검증 보강 | smyun | 250709
        if (key == "id") {
            // 중복확인 버튼 활성화/비활성화
            const checkResult = validateEmailId(value);
            setIsValidHost(checkResult); // 이메일(Id) 유효성 검사

            // id or domain 하나라도 통과 못하면 비활성화
            setIdIsDuplicateDisabled(!checkResult || !isValidDomain);
        } else if (key == "host") {
            const checkResult = validateDomain(value);
            setIsValidDomain(checkResult); // 이메일(도메인) 유효성 검사

            // id or domain 하나라도 통과 못하면 비활성화
            setIdIsDuplicateDisabled(!checkResult || !isValidHost);
        } else if (key == "email_select") {
            if (value == "") {
                // 직접 입력 선택 시 빈 값
                setIdIsDuplicateDisabled(true);
            }

            key = "host";
            setIsValidDomain(value !== ""); // 유효성 검사
            setEmailDisabled(value !== ""); // 이메일 입력칸 활성/비활성화

            // if (registerInfo.id && value !== "") { // 이름이 입력되어 있으면
            if (isValidHost && value !== "") {
                // id 검증 통과 시
                setIdIsDuplicateDisabled(false); // 중복확인버튼 활성화
            } else {
                setIdIsDuplicateDisabled(true); // 중복확인버튼 비활성화
            }
        } else if (key == "password") {
            setIsValidPassword(validatePassword(value)); // 비밀번호 유효성 검사
            // 비밀번호 원본을 수정하면 비밀번호 확인값과 재비교 추가 | smyun | 250905
            setIsPasswordMatch(value === registerInfo.password_check);
        } else if (key == "name") {
            setIsValidName(validateName(value)); // 이름 유효성 검사
        }

        setRegisterInfo({
            ...registerInfo,
            [key]: value,
        });
    };

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    // 아이디 중복확인
    const [idIsDuplicateDisabled, setIdIsDuplicateDisabled] = useState(false);
    const isDuplicate = async (e: BaseSyntheticEvent) => {
        if (registerInfo.id !== "" && registerInfo.host !== "") {
            const userId = registerInfo.id + "@" + registerInfo.host;
            const accountManager = AccountManager.getInstance();
            const registerUserResult = await accountManager.checkUserExists(userId);

            if (registerUserResult.flag) {
                if (registerUserResult.result == "0") {
                    showModal("사용 가능한 아이디 입니다.");
                    setIdIsDuplicateDisabled(true);
                } else {
                    showModal("이미 존재하는 아이디 입니다.");
                    setIdIsDuplicateDisabled(false);
                }
            } else {
                showModal("아이디 중복확인에 실패했습니다. 다시 시도해주세요.");
            }
        } else {
            showModal("이메일을 정확히 입력해주세요.");
        }
    };

    // 비밀번호 확인
    const passwordMatchCheck = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setRegisterInfo((prevState) => ({
            ...prevState,
            password_check: e.target.value,
        }));
        setIsPasswordMatch(e.target.value === registerInfo.password);
    };

    // 이용약관, 개인정보 수집동의 체크박스 클릭
    const checkBoxClicked = (e: ChangeEvent<HTMLInputElement>) => {
        var key = e.target.name;
        var checked = e.target.checked;

        setRegisterInfo({
            ...registerInfo,
            [key]: checked,
        });
    };

    // 회원가입 버튼
    const registerBtnClicked = (e: BaseSyntheticEvent) => {
        if (registerInfo.id == "") {
            showModal("아이디를 입력해주세요.");
        } else if (registerInfo.host == "") {
            showModal("이메일을 입력해주세요.");
        } else if (!isValidHost || !isValidDomain) {
            showModal("이메일 주소를 확인해주세요.");
        } else if (!idIsDuplicateDisabled) {
            showModal("이메일 중복확인을 진행해주세요.");
        } else if (registerInfo.password == "") {
            showModal("비밀번호를 입력해주세요.");
        } else if (!isValidPassword) {
            showModal("비밀번호는 영문, 숫자를 포함한 8~16자리 이내만 가능합니다.");
        } else if (!isPasswordMatch) {
            showModal("비밀번호가 일치하지 않습니다.");
        } else if (registerInfo.name == "") {
            showModal("이름을 입력해주세요.");
        } else if (!isValidName) {
            showModal("이름은 공백을 제외한 한글과 알파벳만 허용됩니다.");
        } else if (registerInfo.year == "" || registerInfo.month == "" || registerInfo.day == "") {
            showModal("생년월일을 입력해주세요.");
        } else if (!registerInfo.policyAccepted) {
            showModal("모두의 지도 이용약관에 동의해주세요.");
        } else if (!registerInfo.privacyAccepted) {
            showModal("개인정보 수집 및 이용약관에 동의해주세요.");
        } else {
            registerUser();
        }
    };

    // 25.08.27 | Kim Young Jun | platform 값에 따른 회원가입 완료 메시지 분기
    // 회원가입 API
    const registerUser = async () => {
        const accountManager = AccountManager.getInstance();
        const registerUserResult = await accountManager.registerUser(registerInfo);

        console.log(registerUserResult.result);

        if (registerUserResult.flag) {
            // result가 객체인지 문자열인지 확인
            const result = registerUserResult.result;
            const platform = typeof result === "object" && result !== null ? result.platform : null;
            const isWebPlatform = platform === 1;

            openModal({
                title: "안내",
                children: (
                    <>
                        회원가입이 완료되었습니다.
                        {!isWebPlatform && (
                            <>
                                <br />
                                가입하신 이메일에서 계정을 활성화 시켜주세요.
                            </>
                        )}
                    </>
                ),
                onCloseNavigatePath: path.ROOT, // 모달창 닫히면 메인페이지로 이동
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        } else {
            showModal("회원가입에 실패하였습니다.");
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.tit_bx}>
                <h2 className={[styles.tit, styles.blue].join(" ")}>회원가입</h2>
            </div>

            <div className={styles.regist_bx}>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            아이디 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        {/* <input className={styles.form_control} name="id" placeholder={'아이디'} value={registerInfo.id} onChange={handleFormChange} /> */}
                        <TextInput
                            name="id"
                            placeholder="아이디"
                            value={registerInfo.id}
                            onChange={handleFormChange}
                            isValid={isValidHost}
                            validMsg={"영문, 숫자, 일부 특수문자._- 포함 4~30자리 이내"}
                            width="30%"
                        />
                        <p>@</p>
                        <TextInput
                            name="host"
                            placeholder="이메일"
                            value={registerInfo.host}
                            onChange={handleFormChange}
                            isDisabled={emailDisabled}
                            isValid={isValidDomain}
                            validMsg={"이메일 형식을 확인해주세요."}
                            width="40%"
                        />
                        <select className={`${styles.email_select} ${styles.sign_up_select}`} onChange={handleFormChange} name="email_select">
                            {selectEmailOptions.map((option) => {
                                return (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <div className={`${styles.signup_btn_container}`} style={{ width: "25%" }}>
                        <button onClick={isDuplicate} disabled={idIsDuplicateDisabled}>
                            중복확인
                        </button>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            비밀번호 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <TextInput
                            type="password"
                            name="password"
                            placeholder="비밀번호"
                            value={registerInfo.password}
                            onChange={handleFormChange}
                            isValid={isValidPassword}
                            validMsg={"영문, 숫자를 포함한 8~16자리 이내로 입력해주세요."}
                        />
                    </div>
                    <div style={{ width: "25%" }}>
                        <span className={styles.form_desc}>※ 영문, 숫자를 포함한 8 ~ 16자리 이내</span>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            비밀번호 확인 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <TextInput
                            type="password"
                            name="password_check"
                            placeholder="비밀번호 확인"
                            value={registerInfo.password_check}
                            onChange={passwordMatchCheck}
                            isValid={isPasswordMatch}
                            validMsg={"비밀번호가 일치하지 않습니다."}
                        />
                    </div>
                    <div style={{ width: "25%" }} />
                </div>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            이름 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <TextInput
                            name="name"
                            placeholder="이름"
                            value={registerInfo.name}
                            onChange={handleFormChange}
                            isValid={isValidName}
                            validMsg={"이름은 공백을 제외한 한글과 알파벳만 허용됩니다."}
                        />
                    </div>
                    <div className={`${styles.btn_group} ${styles.row}`} style={{ width: "25%" }} role="group">
                        <input
                            type="radio"
                            className={styles.btn_check}
                            name="sex"
                            id="male"
                            value="1"
                            checked={registerInfo.sex === "1"}
                            onChange={handleFormChange}
                        />
                        <label htmlFor="male">남</label>
                        <input
                            type="radio"
                            className={styles.btn_check}
                            name="sex"
                            id="female"
                            value="0"
                            checked={registerInfo.sex === "0"}
                            onChange={handleFormChange}
                        />
                        <label htmlFor="female">여</label>
                    </div>
                </div>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.info_tit_bx}>
                        <p className={styles.info_tit}>
                            생년월일 <span className={styles.required}>*</span>
                        </p>
                    </div>
                    <div className={`${styles.row}`}>
                        <select className={`${styles.sign_up_select}`} name="year" onChange={handleFormChange}>
                            <option value="" disabled selected>
                                년
                            </option>
                            {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, index) => 1900 + index)
                                .reverse()
                                .map((date) => (
                                    <option key={date} value={date}>
                                        {date}
                                    </option>
                                ))}
                        </select>
                        <select className={`${styles.sign_up_select}`} name="month" onChange={handleFormChange}>
                            <option value="" disabled selected>
                                월
                            </option>
                            {Array.from({ length: 12 }, (_, index) => index + 1).map((date) => (
                                <option key={date} value={date}>
                                    {date}
                                </option>
                            ))}
                        </select>
                        <select className={`${styles.sign_up_select}`} name="day" onChange={handleFormChange}>
                            <option value="" disabled selected>
                                일
                            </option>
                            {Array.from({ length: 31 }, (_, index) => index + 1).map((date) => (
                                <option key={date} value={date}>
                                    {date}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={{ width: "25%" }} />
                </div>
            </div>
            <div className={`${styles.accepted_container}`}>
                <div className={`${styles.accepted_bx} ${styles.row}`}>
                    <label>
                        모두의 지도 서비스 이용약관 동의
                        <input
                            type="checkbox"
                            name="policyAccepted"
                            checked={registerInfo.policyAccepted}
                            onClick={() =>
                                openModal({
                                    title: "모두의 지도 서비스 이용약관 동의",
                                    children: <PolicyModal checkBoxClicked={checkBoxClicked} isAccepted={registerInfo.policyAccepted} />,
                                    modalButtons: (
                                        <ModalButtons
                                            buttonCount={1}
                                            buttons={[
                                                {
                                                    label: "확인",
                                                    color: "Primary",
                                                    onClick: () => closeModal(),
                                                },
                                            ]}
                                        />
                                    ),
                                    size: "wide",
                                })
                            }
                        />
                    </label>
                </div>
                <div className={`${styles.accepted_bx} ${styles.row}`}>
                    <label>
                        개인정보 수집 및 이용에 대한 안내
                        <input
                            type="checkbox"
                            name="privacyAccepted"
                            checked={registerInfo.privacyAccepted}
                            onClick={() =>
                                openModal({
                                    title: "개인정보 수집 및 이용에 대한 안내",
                                    children: (
                                        <PrivacyModal
                                            onClose={() => closeModal()}
                                            checkBoxClicked={checkBoxClicked}
                                            isAccepted={registerInfo.privacyAccepted}
                                        />
                                    ),
                                    modalButtons: (
                                        <ModalButtons
                                            buttonCount={1}
                                            buttons={[
                                                {
                                                    label: "확인",
                                                    color: "Primary",
                                                    onClick: () => closeModal(),
                                                },
                                            ]}
                                        />
                                    ),
                                    size: "wide",
                                })
                            }
                        />
                    </label>
                </div>
            </div>
            <div className={`${styles.signup_btn_container}`} style={{ marginTop: "30px" }}>
                <button onClick={registerBtnClicked}>회원가입</button>
            </div>

            <ModalComponent />
        </div>
    );
}

export default SignUp;
